import { useEffect, useRef, useState } from "react";
import { Search, ChevronDown, ChevronUp } from "lucide-react";
import { foldIncludes } from "../lib/trSearch";
import CheckListExcel from "./CheckListExcel";

const match = (o, q) => foldIncludes(o.label, q);
const filterOpts = (options, value, q) => options.filter((o) => !q.trim() || value.includes(o.value) || match(o, q));
const FOCUSABLE = 'input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';
const keepFocus = (e) => e.preventDefault();

// Odağı listenin dışındaki bir sonraki form alanına taşır
const focusNext = (box) => {
  const scope = box.closest("form") || document;
  const next = [...scope.querySelectorAll(FOCUSABLE)].find((el) => !box.contains(el) && el.offsetParent !== null && (box.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING));
  next?.focus();
};

// Onay kutulu çoklu seçim listesi (arama + tümünü seç/temizle). Liste kapalı gelir; arama kutusuna tıklayınca/odaklanınca açılır.
// Klavye: ↑↓ gez, Boşluk seç, Enter seç + listeyi kapat + sonraki kutu, ESC kapat. Altta "Listeyi kapat" düğmesi.
export default function CheckList({ options, value = [], onChange, testid, placeholder = "Ara…", emptyText = "Kayıt yok", height = "h-40", excelTitle }) {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(-1);
  const [open, setOpen] = useState(false);
  const navRef = useRef(false);
  const boxRef = useRef(null);
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const shown = filterOpts(options, value, q);
  const allShownOn = shown.length > 0 && shown.every((o) => value.includes(o.value));
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const toggleAll = () => onChange(allShownOn ? value.filter((v) => !shown.some((o) => o.value === v)) : [...new Set([...value, ...shown.map((o) => o.value)])]);
  const close = () => { setOpen(false); setHi(-1); };
  const selectedLabels = value.map((v) => options.find((o) => o.value === v)?.label || v);

  useEffect(() => { if (hi >= shown.length) setHi(shown.length - 1); }, [hi, shown.length]);
  useEffect(() => { if (hi >= 0) rowRefs.current[hi]?.scrollIntoView({ block: "nearest" }); }, [hi]);

  const onSearch = (e) => {
    const v = e.target.value;
    setQ(v); setOpen(true);
    navRef.current = false;
    setHi(v.trim() ? filterOpts(options, value, v).findIndex((o) => match(o, v)) : -1);
  };
  const onKey = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (!shown.length) return;
      navRef.current = true;
      setHi((h) => Math.max(0, Math.min(shown.length - 1, h + (e.key === "ArrowDown" ? 1 : -1))));
    } else if (e.key === " " && open && hi >= 0 && (navRef.current || !q.trim())) {
      e.preventDefault();
      toggle(shown[hi].value);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && hi >= 0 && shown[hi]) toggle(shown[hi].value);
      close();
      focusNext(boxRef.current);
    } else if (e.key === "Escape") {
      if (open) { e.preventDefault(); e.stopPropagation(); close(); }
    }
  };
  const clickRow = (o, i) => { toggle(o.value); setHi(i); navRef.current = true; searchRef.current?.focus(); };
  const onBlur = (e) => { if (!boxRef.current?.contains(e.relatedTarget)) close(); };

  return (
    <div ref={boxRef} onBlur={onBlur} className="border border-slate-300 rounded-md bg-white" data-testid={testid} data-open={open} data-esc-trap={open || undefined}>
      <div className="flex items-center gap-2 p-1.5 border-b border-slate-200 bg-slate-50">
        <div className="relative flex-1">
          <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input ref={searchRef} value={q} onChange={onSearch} onKeyDown={onKey} onFocus={() => setOpen(true)} onClick={() => setOpen(true)} placeholder={placeholder} title="Tıkla: listeyi aç · ↑↓ gez · Boşluk seç · Enter seç, kapat ve sonraki kutuya geç · ESC kapat" data-testid={`${testid}-search`} aria-expanded={open} className="w-full pl-6 pr-2 py-1 text-xs border border-slate-300 rounded-md bg-white outline-none focus:border-amber-500" />
        </div>
        <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={toggleAll} disabled={!shown.length} data-testid={`${testid}-all`} className="px-2 py-1 rounded-md border border-slate-300 text-[11px] font-semibold text-slate-700 hover:bg-white disabled:opacity-40">{allShownOn ? "Seçimi kaldır" : q.trim() ? "Görünenleri seç" : "Tümünü seç"}</button>
        {value.length > 0 && <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={() => onChange([])} data-testid={`${testid}-clear`} className="px-2 py-1 rounded-md border border-red-300 text-[11px] font-semibold text-red-700 hover:bg-red-50">Temizle</button>}
        {excelTitle && <CheckListExcel options={options} value={value} onChange={onChange} title={excelTitle} testid={testid} />}
      </div>
      {open && (
        <>
          <div className={`${height} overflow-y-auto p-1`} role="listbox" aria-multiselectable="true" data-testid={`${testid}-list`}>
            {shown.length === 0 && <div className="text-[11px] text-slate-400 px-2 py-3">{emptyText}</div>}
            {shown.map((o, i) => {
              const on = value.includes(o.value);
              const hl = i === hi;
              return (
                <div key={o.value} ref={(el) => { rowRefs.current[i] = el; }} role="option" aria-selected={on} data-highlighted={hl || undefined} onMouseDown={keepFocus} onClick={() => clickRow(o, i)}
                  className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs select-none ${on ? "bg-amber-50 text-amber-900 font-semibold" : "text-slate-700 hover:bg-slate-50"} ${hl ? "ring-1 ring-inset ring-amber-500 !bg-amber-100" : ""}`} data-testid={`${testid}-opt-${o.value}`}>
                  <input type="checkbox" tabIndex={-1} readOnly checked={on} className="accent-amber-600 w-3.5 h-3.5 pointer-events-none" />
                  <span className="truncate">{o.label}</span>
                </div>
              );
            })}
          </div>
          <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={() => { close(); searchRef.current?.focus(); }} data-testid={`${testid}-close`}
            className="w-full flex items-center justify-center gap-1 px-2 py-1.5 border-t border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"><ChevronUp className="w-3.5 h-3.5" /> Listeyi kapat <span className="text-[9px] text-slate-400 font-normal">(ESC)</span></button>
        </>
      )}
      <div className="px-2 py-1 border-t border-slate-200 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap" data-testid={`${testid}-count`}>
        <span className="shrink-0">{value.length} seçili · {options.length} kayıt</span>
        {!open && selectedLabels.slice(0, 6).map((l) => <span key={l} className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-900 text-[10px] font-semibold truncate max-w-[160px]" data-testid={`${testid}-chip`}>{l}</span>)}
        {!open && selectedLabels.length > 6 && <span className="text-[10px]">+{selectedLabels.length - 6}</span>}
        {!open && <button type="button" tabIndex={-1} onMouseDown={keepFocus} onClick={() => { setOpen(true); searchRef.current?.focus(); }} data-testid={`${testid}-open`} className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-semibold text-slate-600 hover:text-slate-900"><ChevronDown className="w-3 h-3" /> Listeyi aç</button>}
      </div>
    </div>
  );
}
